import type { DomainPurchaseView, PurchaseEvent, PurchaseItemView } from '@/types/api';

/** Buying domains (ADR-0039, ADR-0053) in the owner's words. */

/** The wizard's steps: where a purchase is, from the names typed to the result. */
export type PurchaseStage = 'names' | 'checking' | 'choose' | 'buying' | 'done';

export const PURCHASE_STAGES: readonly { id: PurchaseStage; label: string }[] = [
  { id: 'names', label: 'Имена' },
  { id: 'checking', label: 'Проверка' },
  { id: 'choose', label: 'Выбор и оплата' },
  { id: 'buying', label: 'Покупка и DNS' },
  { id: 'done', label: 'Готово' },
];

export function purchaseStage(view: DomainPurchaseView | undefined): PurchaseStage {
  if (!view) return 'names';
  if (view.finished) return 'done';
  if (view.awaitingConfirmation) return 'choose';
  if (view.status === 'PURCHASING' || view.status === 'PROVISIONING_DNS') return 'buying';
  return 'checking';
}

/** The names paid for: as confirmed in the panel, or those 2d2s marked bought. */
export function chosenItems(view: DomainPurchaseView): PurchaseItemView[] {
  const chosen = new Set(view.chosen);
  return view.items.filter((item) => chosen.has(item.domain) || item.purchased);
}

const ITEM_STATUS: Record<string, string> = {
  PURCHASING: 'покупается',
  PURCHASED: 'куплен',
  PROVISIONING: 'настройка DNS',
  PROVISIONING_DNS: 'настройка DNS',
  PROVISIONED: 'готов',
  COMPLETED: 'готов',
  ACTIVE: 'готов',
  FAILED: 'не куплен',
  SHORTLISTED: 'в шорт-листе',
  AVAILABLE: 'свободен',
  UNAVAILABLE: 'занят',
  TAKEN: 'занят',
};

/** Where one name is while it is bought: paid for, its DNS being made, ready. */
export function buyingState(item: PurchaseItemView, view: DomainPurchaseView): { label: string; tone: 'running' | 'done' | 'failed' | 'pending' } {
  if (item.failure) return { label: item.failure, tone: 'failed' };
  if (item.purchased) {
    if (view.status === 'PROVISIONING_DNS') return { label: 'куплен · настройка DNS', tone: 'running' };
    if (view.finished) return { label: 'готов', tone: 'done' };
    return { label: 'куплен', tone: 'done' };
  }
  if (view.finished) return { label: 'не куплен', tone: 'failed' };
  if (view.status === 'PURCHASING') return { label: 'покупается', tone: 'running' };
  return { label: 'ожидает', tone: 'pending' };
}

/** Where one name is while availability is checked. */
export function checkingState(item: PurchaseItemView): string {
  if (item.available === true) return 'свободен';
  if (item.available === false) return 'занят';
  return 'проверяется';
}

const STATUS_TEXT: Record<string, string> = {
  QUEUED: 'В очереди у 2d2s',
  CHECKING_AVAILABILITY: '2d2s проверяет доступность',
  PURCHASING: 'Оплата: 2d2s покупает домены',
  PROVISIONING_DNS: 'Настройка DNS: зоны Cloudflare и NS',
  COMPLETED: 'Покупка завершена',
  PARTIALLY_COMPLETED: 'Куплено частично',
  FAILED: 'Покупка не состоялась',
  CANCELLED: 'Покупка отменена',
};

function money(amount: number | null | undefined, currency = 'USD'): string {
  if (amount === null || amount === undefined) return 'цена не сообщается';
  return new Intl.NumberFormat('ru-RU', { style: 'currency', currency, maximumFractionDigits: 2 }).format(amount);
}

/** A line of the purchase's history. */
export function purchaseEventText(event: PurchaseEvent): string {
  switch (event.code) {
    case 'created':
      return `Проверка начата: ${event.names?.length ?? 0} имён${event.by ? ` · ${event.by}` : ''}`;
    case 'confirmed':
      return `Покупка подтверждена: ${(event.names ?? []).join(', ')} · ${money(event.cost, event.currency)}${event.by ? ` · ${event.by}` : ''}`;
    case 'cancelled':
      return `Отмена запрошена${event.by ? ` · ${event.by}` : ''}`;
    case 'payment_unknown':
      return `Оплата запрошена, ответ 2d2s не получен: ${(event.names ?? []).join(', ')} · повторная оплата заблокирована${event.by ? ` · ${event.by}` : ''}`;
    case 'item': {
      const status = event.status ? (ITEM_STATUS[event.status] ?? event.status.toLowerCase()) : '';
      const label = event.failure ? `ошибка: ${event.failure}` : event.purchased && status !== 'готов' && status !== 'настройка DNS' ? 'куплен' : status;
      return `${event.domain}: ${label}`;
    }
    case 'status': {
      if (event.status === 'SHORTLIST_READY') return `Проверка завершена: свободно ${event.free ?? 0} из ${event.total ?? 0}`;
      const text = STATUS_TEXT[event.status ?? ''] ?? event.status ?? '';
      return event.failure ? `${text}: ${event.failure}` : text;
    }
  }
}

/** How far the purchase has got, for the bar: 2d2s's percent while it counts one, else the names done. */
export function purchaseProgress(view: DomainPurchaseView): { value: number; max: number } {
  const stage = purchaseStage(view);
  if (stage === 'checking') {
    if (view.progress !== null) return { value: view.progress, max: 100 };
    return { value: view.items.filter((item) => item.available !== null).length, max: Math.max(1, view.items.length) };
  }
  if (stage === 'buying') {
    const chosen = chosenItems(view);
    if (view.progress !== null) return { value: view.progress, max: 100 };
    const bought = chosen.filter((item) => item.purchased).length;
    // Half for paying, half for the DNS.
    return { value: bought + (view.status === 'PROVISIONING_DNS' ? bought * 0.5 : 0), max: Math.max(1, chosen.length * 1.5) };
  }
  return { value: stage === 'done' ? 1 : 0, max: 1 };
}

export function formatMoney(amount: number | null, currency: string): string {
  return money(amount, currency);
}
